import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Button,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import LanguageIcon from '@mui/icons-material/Language';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import CheckIcon from '@mui/icons-material/Check';

const languages = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧' },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷' },
];

const StyledButton = styled(Button, { shouldForwardProp: (prop) => prop !== 'light' })(({ theme, light }) => ({
  color: light ? '#FFFFFF' : theme.palette.text.primary,
  textTransform: 'none',
  fontWeight: 500,
  padding: '6px 12px',
  minWidth: 'auto',
  borderRadius: '8px',
  border: light ? '1px solid rgba(255, 255, 255, 0.2)' : '1px solid rgba(0, 0, 0, 0.1)',

  '&:hover': {
    backgroundColor: light ? 'rgba(255, 255, 255, 0.1)' : 'rgba(0, 0, 0, 0.04)',
    borderColor: light ? 'rgba(255, 255, 255, 0.4)' : 'rgba(0, 0, 0, 0.2)',
  },

  '& .MuiButton-endIcon': {
    marginLeft: 4,
  },
}));

const StyledMenu = styled(Menu)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: '12px',
    marginTop: theme.spacing(1),
    minWidth: 160,
    boxShadow: '0 4px 20px rgba(0, 0, 0, 0.15)',
  },
}));

const StyledMenuItem = styled(MenuItem)(({ theme, selected }) => ({
  padding: '10px 16px',
  borderRadius: '8px',
  margin: '4px 8px',

  ...(selected && {
    backgroundColor: 'rgba(0, 184, 212, 0.08)',
    color: theme.palette.primary.main,
  }),

  '&:hover': {
    backgroundColor: 'rgba(0, 184, 212, 0.12)',
  },
}));

export default function LanguageSwitcher({ light = false }) {
  const { i18n } = useTranslation();
  const [anchorEl, setAnchorEl] = useState(null);
  const open = Boolean(anchorEl);

  const currentLanguage = languages.find(lang => lang.code === i18n.language) || languages[0];

  const handleClick = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleLanguageChange = (langCode) => {
    i18n.changeLanguage(langCode);
    handleClose();
  };

  return (
    <>
      <StyledButton
        light={light ? 1 : 0}
        onClick={handleClick}
        startIcon={<LanguageIcon sx={{ fontSize: '18px' }} />}
        endIcon={<KeyboardArrowDownIcon sx={{ fontSize: '18px' }} />}
        aria-controls={open ? 'language-menu' : undefined}
        aria-haspopup="true"
        aria-expanded={open ? 'true' : undefined}
      >
        <Typography
          component="span"
          sx={{
            fontSize: '0.875rem',
            display: { xs: 'none', sm: 'inline' },
          }}
        >
          {currentLanguage.nativeName}
        </Typography>
        <Typography
          component="span"
          sx={{
            fontSize: '0.875rem',
            display: { xs: 'inline', sm: 'none' },
          }}
        >
          {currentLanguage.code.toUpperCase()}
        </Typography>
      </StyledButton>

      <StyledMenu
        id="language-menu"
        anchorEl={anchorEl}
        open={open}
        onClose={handleClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
      >
        {languages.map((language) => (
          <StyledMenuItem
            key={language.code}
            selected={i18n.language === language.code}
            onClick={() => handleLanguageChange(language.code)}
          >
            <ListItemIcon sx={{ minWidth: '32px', fontSize: '1.2rem' }}>
              {language.flag}
            </ListItemIcon>
            <ListItemText
              primary={language.nativeName}
              primaryTypographyProps={{
                fontSize: '0.9rem',
                fontWeight: i18n.language === language.code ? 600 : 400,
              }}
            />
            {i18n.language === language.code && (
              <CheckIcon sx={{ fontSize: '18px', color: 'primary.main', ml: 1 }} />
            )}
          </StyledMenuItem>
        ))}
      </StyledMenu>
    </>
  );
}
